import type { TFactTrajectoryData } from './fact-trajectory.entity';

export type TComparableTrajectorySnapshot = {
  title: string;
  description: string | null;
  organizationId: number | null;
  active: boolean;
  finalTraj: boolean | null;
  parentTrajectoryId: number | null;
  mdTieOn: number | null;
  aziRef: string | null;
  gridConUsed: number | null;
  magDeclUsed: number | null;
  aziVertSect: number | null;
  defaultMdDatumUuid: string | null;
  defaultTvdDatumUuid: string | null;
  depthUomId: number | null;
  severityUomId: number | null;
  coordinatesUomId: number | null;
};

function readMeasureValue(measure: { value?: number } | undefined | null): number | null {
  if (typeof measure?.value === 'number' && !Number.isNaN(measure.value)) {
    return measure.value;
  }

  return null;
}

function readAngleValue(value: { value?: number } | number | undefined | null): number | null {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  if (value && typeof value === 'object' && typeof value.value === 'number' && !Number.isNaN(value.value)) {
    return value.value;
  }

  return null;
}

function readDatumUuid(
  datum: { baseReferencePoint?: { uuid?: unknown } | Record<string, unknown> } | null | undefined,
): string | null {
  const base = datum?.baseReferencePoint;

  if (!base || typeof base !== 'object' || !('uuid' in base)) {
    return null;
  }

  return typeof base.uuid === 'string' && base.uuid.length > 0 ? base.uuid : null;
}

function normalizeNullableString(value: string | null | undefined): string | null {
  if (value === undefined || value === null) {
    return null;
  }

  const trimmed = value.trim();

  return trimmed.length > 0 ? trimmed : null;
}

export function toComparableTrajectorySnapshot(data: TFactTrajectoryData): TComparableTrajectorySnapshot {
  return {
    title: (data.citation?.title ?? '').trim(),
    description: normalizeNullableString(data.citation?.description),
    organizationId: data.organizationId ?? null,
    active: Boolean(data.active ?? data.definitive),
    finalTraj: data.finalTraj ?? null,
    parentTrajectoryId: data.parentTrajectoryId ?? null,
    mdTieOn: readMeasureValue(data.mdTieOn?.measuredDepth),
    aziRef: data.aziRef ?? null,
    gridConUsed: readAngleValue(data.gridConUsed),
    magDeclUsed: readAngleValue(data.magDeclUsed),
    aziVertSect: readAngleValue(data.aziVertSect),
    defaultMdDatumUuid: readDatumUuid(data.defaultMdDatum),
    defaultTvdDatumUuid: readDatumUuid(data.defaultTvdDatum),
    depthUomId: data.depthUomId ?? null,
    severityUomId: data.severityUomId ?? null,
    coordinatesUomId: data.coordinatesUomId ?? null,
  };
}

export function areComparableTrajectorySnapshotsEqual(
  left: TComparableTrajectorySnapshot,
  right: TComparableTrajectorySnapshot,
): boolean {
  return (
    left.title === right.title &&
    left.description === right.description &&
    left.organizationId === right.organizationId &&
    left.active === right.active &&
    left.finalTraj === right.finalTraj &&
    left.parentTrajectoryId === right.parentTrajectoryId &&
    left.mdTieOn === right.mdTieOn &&
    left.aziRef === right.aziRef &&
    left.gridConUsed === right.gridConUsed &&
    left.magDeclUsed === right.magDeclUsed &&
    left.aziVertSect === right.aziVertSect &&
    left.defaultMdDatumUuid === right.defaultMdDatumUuid &&
    left.defaultTvdDatumUuid === right.defaultTvdDatumUuid &&
    left.depthUomId === right.depthUomId &&
    left.severityUomId === right.severityUomId &&
    left.coordinatesUomId === right.coordinatesUomId
  );
}
